import { Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2 } from '@angular/core';

/**
 * Shows a styled hover popup with the full text of the host element, but ONLY
 * when that element is visually truncated (CSS ellipsis). When the text fits,
 * no popup is shown. The popup is appended to <body> so it is never clipped by
 * the table's horizontal scroll container or overflow rules.
 *
 * Usage:
 *   <span class="cell-text" [appTruncatedTooltip]="fullValue">{{ shortValue }}</span>
 */
@Directive({
    selector: '[appTruncatedTooltip]',
    standalone: false
})
export class TruncatedTooltipDirective implements OnDestroy {
  /** Full text to display in the popup when the host is truncated. */
  @Input('appTruncatedTooltip') tooltipText: string | null = '';

  private tooltipEl: HTMLElement | null = null;
  private removeScrollListener?: () => void;
  private removeResizeListener?: () => void;

  constructor(private host: ElementRef<HTMLElement>, private renderer: Renderer2) { }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    const text = (this.tooltipText ?? '').toString().trim();
    if (!text) {
      return;
    }
    if (!this.isTruncated() && !this.isContentShortened(text)) {
      return;
    }
    this.show(text);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hide();
  }

  /**
   * Reposition/hide while scrolling so the popup never floats detached from
   * its cell (the table lives inside a scrollable container).
   */
  // @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChange(): void {
    this.hide();
  }

  /**
   * True when the host's content overflows its box in either axis, i.e. it is
   * being clipped with an ellipsis.
   * @returns Whether the host element is currently truncated.
   */
  private isTruncated(): boolean {
    const el = this.host.nativeElement;
    return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
  }

  /**
   * True when the host's visible text is a shortened form of the full value,
   * e.g. an address/identifier collapsed to `0x1234...5678` or a preview that
   * ends in an ellipsis. Covers cases where the content is trimmed in code
   * (so the box is not overflowing) yet the full value should still surface on
   * hover. The visible text must carry an explicit ellipsis marker so we do NOT
   * treat mere formatting differences (currency, casing, whitespace) as
   * truncation — that would surface a tooltip on cells whose full value is
   * already visible.
   * @param full The complete value the tooltip would display.
   * @returns Whether the rendered text is an ellipsised shortening of the full value.
   */
  private isContentShortened(full: string): boolean {
    const shown = (this.host.nativeElement.textContent ?? '').trim();
    if (!shown || shown === full) {
      return false;
    }
    return shown.includes('...') || shown.includes('…');
  }

  /**
   * Builds the popup, appends it to <body>, then positions it below the host
   * centred horizontally, clamped to the viewport.
   * @param text Full text to render inside the popup.
   */
  private show(text: string): void {
    this.hide();

    const tooltip = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(tooltip, 'rw-truncate-tooltip');
    this.renderer.setProperty(tooltip, 'textContent', text);
    this.renderer.appendChild(document.body, tooltip);
    this.tooltipEl = tooltip;
    this.addScrollListener();

    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const tipRect = tooltip.getBoundingClientRect();
    const gap = 10;
    const margin = 8;

    let top = hostRect.bottom + gap;
    // Flip above the host when there isn't room below.
    if (top + tipRect.height + margin > window.innerHeight) {
      top = hostRect.top - tipRect.height - gap;
      this.renderer.addClass(tooltip, 'rw-truncate-tooltip--above');
    }

    let left = hostRect.left + hostRect.width / 2 - tipRect.width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - tipRect.width - margin));

    this.renderer.setStyle(tooltip, 'top', `${Math.round(top)}px`);
    this.renderer.setStyle(tooltip, 'left', `${Math.round(left)}px`);
  }

  private hide(): void {
    if (this.tooltipEl) {
      this.renderer.removeChild(document.body, this.tooltipEl);
      this.tooltipEl = null;
    }

    this.removeListeners();
  }
  public ngOnDestroy(): void {
    this.hide();
  }

  private addScrollListener(): void {
    const scrollContainer = this.host.nativeElement.closest('.table-responsive');

    if (scrollContainer && !this.removeScrollListener) {
      this.removeScrollListener = this.renderer.listen(
        scrollContainer,
        'scroll',
        () => this.hide()
      );
    }

    if (!this.removeResizeListener) {
      this.removeResizeListener = this.renderer.listen(
        'window',
        'resize',
        () => this.hide()
      );
    }
  }

  private removeListeners(): void {
    this.removeScrollListener?.();
    this.removeScrollListener = undefined;

    this.removeResizeListener?.();
    this.removeResizeListener = undefined;
  }
}
